import { useState } from 'react';
import { Col, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useGeoConfig } from '../../context/GeoConfigContext';

interface Country {
    code: string;
    name: string;
    image: string;
}

interface CountryDropdownProps {
    countriesData: Country[];
    selectedCountry: string | null;
    handleCountryClick: (countryName: string) => void;
    countryValidation: boolean;
    title?: string;
    /** When true (default), blocked countries are hidden from the list */
    filterBlocked?: boolean;
}

const CountryDropdown: React.FC<CountryDropdownProps> = ({
    countriesData,
    selectedCountry,
    handleCountryClick,
    countryValidation,
    title,
    filterBlocked = true,
}) => {
    const { t } = useTranslation();
    const { config } = useGeoConfig();
    const [searchTerm, setSearchTerm] = useState<string>('');

    // Remove blocked countries using the live DB-backed config (updates without rebuild)
    const allowedCountries = filterBlocked
        ? countriesData.filter((c) => !config.blockedCountries.includes(c.code.toUpperCase()))
        : countriesData;

    const filteredCountries = allowedCountries.filter((country) =>
        country.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="mt-4 row mb-3">
            <Col md={12}>
                <label className="form-label fw-bold">{title ?? t('ui.country_label')}</label>

                <div className="dropdown">
                    <Button
                        className={`form-control form-select text-start px-3 rounded-5 border bg-white text-secondary ${countryValidation ? 'border-danger' : ''}`}
                        type="button"
                        name="country"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        variant=""
                    >
                        {selectedCountry ? (
                            <>
                                <img
                                    src={countriesData.find((country) => country.name === selectedCountry)?.image}
                                    alt={selectedCountry || t('ui.country_label')}
                                    style={{ width: '20px', marginRight: '5px' }}
                                />
                                {selectedCountry}
                            </>
                        ) : (
                            t('ui.country_placeholder')
                        )}
                    </Button>
                    <ul className="dropdown-menu w-100 rounded-4 hide-scroll border-0 shadow">
                        <li className="px-3 py-2">
                            <input
                                type="text"
                                className="form-control"
                                placeholder={t('ui.country_search')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </li>
                        {filteredCountries.length > 0 ? (
                            filteredCountries.map((country) => (
                                <li key={country.code}>
                                    <span
                                        className="dropdown-item"
                                        onClick={() => handleCountryClick(country.name)}
                                    >
                                        <img
                                            src={country.image}
                                            alt={country.name}
                                            style={{ width: '20px', marginRight: '5px' }}
                                        />
                                        {country.name}
                                    </span>
                                </li>
                            ))
                        ) : (
                            <li className="dropdown-item text-muted text-center">{t('ui.no_country')}</li>
                        )}
                    </ul>
                </div>
                <div id="country_validation" className={countryValidation ? '' : 'd-none'}>
                    <p className="text-danger small mt-1 ps-2 mb-0 fw-bold">{t('ui.country_required')}</p>
                </div>
            </Col>
        </div>
    );
};

export default CountryDropdown;
